/**
 * PostContent — renders the markdown body of a post card in the feed.
 *
 * Uses the shared <CellCogMarkdown> so prose rendering matches the chat
 * and file-viewer surfaces exactly. Posts are public, so SDK protocol
 * tags (SHOW_FILE / GENERATE_FILE) are not meaningful here and are not
 * resolved — they would appear as literal text if anyone ever typed them
 * into a post, which is the right fallback.
 *
 * Behavior preserved:
 *   - Optional title
 *   - "See more / See less" truncation for long content
 *   - Compact mode (no markdown body)
 *   - Dark-mode aware
 */
import { Button, Typography } from 'antd';
import React, { useState } from 'react';
import CellCogMarkdown from '../../Common/CellCogMarkdown';
import RichCodeBlock from '../../Common/RichCodeBlock';
import './PostContent.css';

const PostContent = ({ content, title, isDarkMode, compact = false }) => {
  const [expanded, setExpanded] = useState(false);

  const shouldTruncate = typeof content === 'string' && content.length > 300;
  const displayContent =
    shouldTruncate && !expanded ? content.substring(0, 300) + '...' : content;

  return (
    <div className={`post-content ${isDarkMode ? 'dark' : 'light'}`}>
      {title && (
        <Typography.Title level={4} className="post-title">
          {title}
        </Typography.Title>
      )}

      {!compact && (
        <div className="post-text">
          <CellCogMarkdown
            content={displayContent || ''}
            isDarkMode={isDarkMode}
            enableCellCogTags="none"
            codeBlockComponent={RichCodeBlock}
            richCodeDefaultMode="raw"
          />

          {shouldTruncate && (
            <Button
              type="link"
              onClick={() => setExpanded(!expanded)}
              className="expand-button"
              size="small"
            >
              {expanded ? 'See less' : 'See more'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default PostContent;
